import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, CheckCircle, Upload, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { settingsApi, uploadApi } from '@/services/api';
import { toast } from 'sonner';
import { sanitizeHtml } from '@/lib/sanitizeHtml';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    phone: '',
    whatsapp: '',
    email: '',
    address_en: '',
    address_ar: '',
    hero_video_url: '',
    hero_title_en: '',
    hero_title_ar: '',
    hero_subtitle_en: '',
    hero_subtitle_ar: '',
    hero_cta_primary_text_en: '',
    hero_cta_primary_text_ar: '',
    hero_cta_primary_link: '',
    hero_cta_secondary_text_en: '',
    hero_cta_secondary_text_ar: '',
    hero_cta_secondary_link: '',
    hero_slides: [],
    hero_slider_size_mode: 'ratio',
    hero_slider_ratio_w: 1920,
    hero_slider_ratio_h: 700,
    hero_slider_height_px: 700,
    hero_slider_min_height_px: 480,
    hero_slider_fit: 'cover',
    social_links: {
      instagram: '',
      facebook: '',
      twitter: ''
    },
    about_layout: 'collage',
    about_images: [],
    about_show_badge: true,
    about_badge_value: '',
    about_badge_label_en: '',
    about_badge_label_ar: '',
    transformation_before_image: '',
    transformation_after_image: '',
    transformation_title_en: '',
    transformation_title_ar: '',
    agreement_template_en: '',
    agreement_template_ar: '',
    footer_description_en: '',
    footer_description_ar: '',
    footer_hours_line1_label_en: '',
    footer_hours_line1_label_ar: '',
    footer_hours_line1_value_en: '',
    footer_hours_line1_value_ar: '',
    footer_hours_line2_label_en: '',
    footer_hours_line2_label_ar: '',
    footer_hours_line2_value_en: '',
    footer_hours_line2_value_ar: '',
    footer_copyright_name: '',
    footer_privacy_url: '',
    footer_terms_url: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings({
        ...response.data,
        social_links: response.data.social_links || { instagram: '', facebook: '', twitter: '' }
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTransformationUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadApi.image(file);
      setSettings((prev) => ({ ...prev, [field]: res.data.url }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    }
    e.target.value = '';
  };

  const aboutImages = settings.about_images || [];

  const setAboutImage = (index, value) => {
    setSettings((prev) => {
      const imgs = [...(prev.about_images || [])];
      imgs[index] = value;
      return { ...prev, about_images: imgs };
    });
  };

  const removeAboutImage = (index) => {
    setSettings((prev) => ({
      ...prev,
      about_images: (prev.about_images || []).filter((_, i) => i !== index),
    }));
  };

  const moveAboutImage = (index, dir) => {
    setSettings((prev) => {
      const imgs = [...(prev.about_images || [])];
      const target = index + dir;
      if (target < 0 || target >= imgs.length) return prev;
      [imgs[index], imgs[target]] = [imgs[target], imgs[index]];
      return { ...prev, about_images: imgs };
    });
  };

  const heroSlides = settings.hero_slides || [];

  const setHeroSlide = (index, value) => {
    setSettings((prev) => {
      const imgs = [...(prev.hero_slides || [])];
      imgs[index] = value;
      return { ...prev, hero_slides: imgs };
    });
  };

  const removeHeroSlide = (index) => {
    setSettings((prev) => ({
      ...prev,
      hero_slides: (prev.hero_slides || []).filter((_, i) => i !== index),
    }));
  };

  const moveHeroSlide = (index, dir) => {
    setSettings((prev) => {
      const imgs = [...(prev.hero_slides || [])];
      const target = index + dir;
      if (target < 0 || target >= imgs.length) return prev;
      [imgs[index], imgs[target]] = [imgs[target], imgs[index]];
      return { ...prev, hero_slides: imgs };
    });
  };

  const handleHeroSlideUpload = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadApi.image(file);
      setHeroSlide(index, res.data.url);
      toast.success('Slide uploaded');
    } catch {
      toast.error('Slide upload failed');
    }
    e.target.value = '';
  };

  const handleAboutImageUpload = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadApi.image(file);
      setAboutImage(index, res.data.url);
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    }
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);

    try {
      await settingsApi.update({ ...settings, id: 'site_settings' });
      setSaved(true);
      toast.success('Settings saved successfully');
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-white/5 w-48" />
        <div className="h-64 bg-white/5" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Site Settings</h1>
        <p className="text-white/60">Configure your website settings</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-8">
        {/* Contact Information */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-6">Contact Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-white/60 text-sm mb-2">Phone Number</label>
              <input
                type="text"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                data-testid="settings-phone"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="+966..."
              />
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">WhatsApp Number</label>
              <input
                type="text"
                value={settings.whatsapp}
                onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })}
                data-testid="settings-whatsapp"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="+966..."
              />
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Email</label>
              <input
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                data-testid="settings-email"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="info@armankitchens.com"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Address (English)</label>
                <input
                  type="text"
                  value={settings.address_en}
                  onChange={(e) => setSettings({ ...settings, address_en: e.target.value })}
                  data-testid="settings-address-en"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Address (Arabic)</label>
                <input
                  type="text"
                  value={settings.address_ar}
                  onChange={(e) => setSettings({ ...settings, address_ar: e.target.value })}
                  data-testid="settings-address-ar"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  dir="rtl"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-6">Social Media Links</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-white/60 text-sm mb-2">Instagram URL</label>
              <input
                type="url"
                value={settings.social_links?.instagram || ''}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  social_links: { ...settings.social_links, instagram: e.target.value } 
                })}
                data-testid="settings-instagram"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="https://instagram.com/..."
              />
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Facebook URL</label>
              <input
                type="url"
                value={settings.social_links?.facebook || ''}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  social_links: { ...settings.social_links, facebook: e.target.value } 
                })}
                data-testid="settings-facebook"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="https://facebook.com/..."
              />
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Twitter/X URL</label>
              <input
                type="url"
                value={settings.social_links?.twitter || ''}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  social_links: { ...settings.social_links, twitter: e.target.value } 
                })}
                data-testid="settings-twitter"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="https://twitter.com/..."
              />
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-2">Hero Section</h2>
          <p className="text-white/40 text-sm mb-6">
            The main headline area at the top of the homepage. Leave a field empty to keep the site default.
          </p>

          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Headline (English)</label>
                <input
                  type="text"
                  value={settings.hero_title_en || ''}
                  onChange={(e) => setSettings({ ...settings, hero_title_en: e.target.value })}
                  data-testid="settings-hero-title-en"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  placeholder="Make The Kitchen Part Of The Family"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Headline (Arabic)</label>
                <input
                  type="text"
                  value={settings.hero_title_ar || ''}
                  onChange={(e) => setSettings({ ...settings, hero_title_ar: e.target.value })}
                  data-testid="settings-hero-title-ar"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  dir="rtl"
                  placeholder="اجعل المطبخ جزءاً من العائلة"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Subtitle (English)</label>
                <textarea
                  rows={2}
                  value={settings.hero_subtitle_en || ''}
                  onChange={(e) => setSettings({ ...settings, hero_subtitle_en: e.target.value })}
                  data-testid="settings-hero-subtitle-en"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none resize-y"
                  placeholder="Kitchens should be designed according to what really matters..."
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Subtitle (Arabic)</label>
                <textarea
                  rows={2}
                  value={settings.hero_subtitle_ar || ''}
                  onChange={(e) => setSettings({ ...settings, hero_subtitle_ar: e.target.value })}
                  data-testid="settings-hero-subtitle-ar"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none resize-y"
                  dir="rtl"
                  placeholder="يجب تصميم المطابخ وفقاً لما يهم حقاً..."
                />
              </div>
            </div>

            <div className="border-t border-white/10 pt-4">
              <p className="text-white/60 text-sm mb-3 font-medium">Primary Button</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-white/50 text-xs mb-2">Text (English)</label>
                  <input
                    type="text"
                    value={settings.hero_cta_primary_text_en || ''}
                    onChange={(e) => setSettings({ ...settings, hero_cta_primary_text_en: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    placeholder="Visit Showroom"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-2">Text (Arabic)</label>
                  <input
                    type="text"
                    value={settings.hero_cta_primary_text_ar || ''}
                    onChange={(e) => setSettings({ ...settings, hero_cta_primary_text_ar: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    dir="rtl"
                    placeholder="زيارة المعرض"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-2">Link</label>
                  <input
                    type="text"
                    value={settings.hero_cta_primary_link || ''}
                    onChange={(e) => setSettings({ ...settings, hero_cta_primary_link: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    placeholder="/projects"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 pt-4">
              <p className="text-white/60 text-sm mb-3 font-medium">Secondary Button</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-white/50 text-xs mb-2">Text (English)</label>
                  <input
                    type="text"
                    value={settings.hero_cta_secondary_text_en || ''}
                    onChange={(e) => setSettings({ ...settings, hero_cta_secondary_text_en: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    placeholder="Get Free Quote"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-2">Text (Arabic)</label>
                  <input
                    type="text"
                    value={settings.hero_cta_secondary_text_ar || ''}
                    onChange={(e) => setSettings({ ...settings, hero_cta_secondary_text_ar: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    dir="rtl"
                    placeholder="احصل على عرض سعر"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-2">Link</label>
                  <input
                    type="text"
                    value={settings.hero_cta_secondary_link || ''}
                    onChange={(e) => setSettings({ ...settings, hero_cta_secondary_link: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    placeholder="/contact"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 pt-4">
              <label className="block text-white/60 text-sm mb-2">Hero Video URL (YouTube)</label>
              <input
                type="url"
                value={settings.hero_video_url || ''}
                onChange={(e) => setSettings({ ...settings, hero_video_url: e.target.value })}
                data-testid="settings-hero-video"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="https://youtube.com/watch?v=..."
              />
              <p className="text-white/40 text-sm mt-2">
                Leave empty to use default image slider
              </p>
            </div>
          </div>
        </div>

        {/* Hero Slider (Homepage) */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-2">Hero Slider (Homepage)</h2>
          <p className="text-white/40 text-sm mb-6">
            The big image slider at the top of the homepage. The headline and buttons above are
            shown on top of it.
          </p>

          <div className="space-y-6">
            {/* Size mode */}
            <div>
              <label className="block text-white/60 text-sm mb-3">Slider Size</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  {
                    value: 'ratio',
                    label: 'Match image ratio',
                    hint: 'Wide banner, no cropping',
                    preview: <div className="bg-white/30 w-full" style={{ aspectRatio: '1920 / 700' }} />,
                  },
                  {
                    value: 'screen',
                    label: 'Full screen',
                    hint: 'Fills the whole window',
                    preview: <div className="bg-white/30 w-full h-16" />,
                  },
                  {
                    value: 'fixed',
                    label: 'Fixed height',
                    hint: 'Exact pixel height',
                    preview: <div className="bg-white/30 w-full h-10" />,
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSettings({ ...settings, hero_slider_size_mode: opt.value })}
                    data-testid={`hero-size-${opt.value}`}
                    className={`p-3 border text-left transition-colors ${
                      (settings.hero_slider_size_mode || 'ratio') === opt.value
                        ? 'border-gold bg-gold/10'
                        : 'border-white/10 bg-white/5 hover:border-white/30'
                    }`}
                  >
                    <div className="h-16 flex items-center">{opt.preview}</div>
                    <span className="block text-white text-sm mt-2 font-medium">{opt.label}</span>
                    <span className="block text-white/40 text-xs">{opt.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Size numbers */}
            <div className="border-t border-white/10 pt-4 space-y-4">
              {(settings.hero_slider_size_mode || 'ratio') === 'ratio' && (
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    Image Ratio (width × height)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      value={settings.hero_slider_ratio_w ?? 1920}
                      onChange={(e) =>
                        setSettings({ ...settings, hero_slider_ratio_w: Number(e.target.value) || 0 })
                      }
                      data-testid="hero-ratio-w"
                      className="w-32 bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:border-gold outline-none"
                    />
                    <span className="text-white/40">×</span>
                    <input
                      type="number"
                      min="1"
                      value={settings.hero_slider_ratio_h ?? 700}
                      onChange={(e) =>
                        setSettings({ ...settings, hero_slider_ratio_h: Number(e.target.value) || 0 })
                      }
                      data-testid="hero-ratio-h"
                      className="w-32 bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:border-gold outline-none"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setSettings({ ...settings, hero_slider_ratio_w: 1920, hero_slider_ratio_h: 700 })
                      }
                      className="text-xs bg-white/10 text-white/70 px-3 py-2 hover:bg-gold/20 hover:text-gold transition-colors"
                    >
                      Use 1920 × 700
                    </button>
                  </div>
                  <p className="text-white/40 text-xs mt-2">
                    The slider keeps this shape at every screen width. 1920 × 700 matches the
                    supplied slide images.
                  </p>
                </div>
              )}

              {(settings.hero_slider_size_mode || 'ratio') === 'fixed' && (
                <div>
                  <label className="block text-white/60 text-sm mb-2">Height (pixels)</label>
                  <input
                    type="number"
                    min="100"
                    value={settings.hero_slider_height_px ?? 700}
                    onChange={(e) =>
                      setSettings({ ...settings, hero_slider_height_px: Number(e.target.value) || 0 })
                    }
                    data-testid="hero-height-px"
                    className="w-40 bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:border-gold outline-none"
                  />
                </div>
              )}

              {(settings.hero_slider_size_mode || 'ratio') !== 'screen' && (
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    Minimum Height on Phones (pixels)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settings.hero_slider_min_height_px ?? 480}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        hero_slider_min_height_px: Number(e.target.value) || 0,
                      })
                    }
                    data-testid="hero-min-height-px"
                    className="w-40 bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:border-gold outline-none"
                  />
                  <p className="text-white/40 text-xs mt-2">
                    Stops the slider from getting too short on narrow screens to fit the headline.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-white/60 text-sm mb-2">Image Fitting</label>
                <div className="flex gap-3">
                  {[
                    { value: 'cover', label: 'Fill (may crop edges)' },
                    { value: 'contain', label: 'Show whole image' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSettings({ ...settings, hero_slider_fit: opt.value })}
                      data-testid={`hero-fit-${opt.value}`}
                      className={`px-4 py-2.5 text-sm border transition-colors ${
                        (settings.hero_slider_fit || 'cover') === opt.value
                          ? 'border-gold bg-gold/10 text-gold'
                          : 'border-white/10 bg-white/5 text-white/70 hover:border-white/30'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Slides */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-white/60 text-sm">Slides</label>
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, hero_slides: [...heroSlides, ''] })}
                  data-testid="hero-add-slide"
                  className="flex items-center gap-1.5 text-xs bg-white/10 text-white/70 px-3 py-1.5 hover:bg-gold/20 hover:text-gold transition-colors"
                >
                  <Plus size={14} /> Add Slide
                </button>
              </div>
              <p className="text-white/40 text-xs mb-3">
                Leave empty to show the 4 default slides. For the sharpest result upload images at
                the same size as the slider ratio above (1920 × 700).
              </p>
              <div className="space-y-3">
                {heroSlides.map((url, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <span className="text-white/40 text-sm pt-3 w-5 text-center">{index + 1}</span>
                    <div className="flex-1">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={url}
                          onChange={(e) => setHeroSlide(index, e.target.value)}
                          className="flex-1 bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:border-gold outline-none text-sm"
                          placeholder="https://... or upload"
                        />
                        <label className="flex items-center gap-2 bg-white/10 text-white/70 px-3 cursor-pointer hover:bg-white/20 transition-colors">
                          <Upload size={15} />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleHeroSlideUpload(e, index)}
                          />
                        </label>
                      </div>
                      {url && (
                        <img
                          src={url}
                          alt={`Slide ${index + 1}`}
                          className="mt-2 h-16 w-auto object-cover border border-white/10"
                        />
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => moveHeroSlide(index, -1)}
                        disabled={index === 0}
                        className="p-1.5 bg-white/5 text-white/50 hover:text-white disabled:opacity-30 transition-colors"
                        aria-label="Move up"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveHeroSlide(index, 1)}
                        disabled={index === heroSlides.length - 1}
                        className="p-1.5 bg-white/5 text-white/50 hover:text-white disabled:opacity-30 transition-colors"
                        aria-label="Move down"
                      >
                        <ArrowDown size={14} />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeHeroSlide(index)}
                      className="p-1.5 mt-1 bg-white/5 text-red-400/70 hover:text-red-400 transition-colors"
                      aria-label="Remove slide"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
                {heroSlides.length === 0 && (
                  <div className="space-y-3">
                    <p className="text-white/30 text-sm italic">
                      No custom slides — the 4 default slides are shown.
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setSettings({
                          ...settings,
                          hero_slides: [
                            '/slides/slid1.jpg',
                            '/slides/slid2.jpg',
                            '/slides/slid3.jpg',
                            '/slides/slid4.jpg',
                          ],
                        })
                      }
                      className="text-xs bg-white/10 text-white/70 px-3 py-1.5 hover:bg-gold/20 hover:text-gold transition-colors"
                    >
                      Load default slides to edit
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* About Section (Homepage) */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-2">About Section (Homepage)</h2>
          <p className="text-white/40 text-sm mb-6">
            Controls the images area of the "About Us" block on the homepage. The texts (title,
            description, features) are edited in <span className="text-gold">Site Content</span> under
            the "about" section.
          </p>

          <div className="space-y-6">
            {/* Layout picker */}
            <div>
              <label className="block text-white/60 text-sm mb-3">Design / Layout</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  {
                    value: 'collage',
                    label: 'Collage',
                    hint: '3 offset photos',
                    preview: (
                      <div className="grid grid-cols-2 gap-1 h-16">
                        <div className="space-y-1">
                          <div className="bg-white/30 h-9" />
                          <div className="bg-white/30 h-6" />
                        </div>
                        <div className="pt-3">
                          <div className="bg-white/30 h-12" />
                        </div>
                      </div>
                    ),
                  },
                  {
                    value: 'slider',
                    label: 'Slider',
                    hint: 'Auto slideshow',
                    preview: (
                      <div className="relative h-16 bg-white/30">
                        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1">
                          <div className="w-3 h-1 bg-gold" />
                          <div className="w-1.5 h-1 bg-white/60" />
                          <div className="w-1.5 h-1 bg-white/60" />
                        </div>
                      </div>
                    ),
                  },
                  {
                    value: 'single',
                    label: 'Single',
                    hint: 'One framed photo',
                    preview: (
                      <div className="relative h-16 mt-1 ml-1">
                        <div className="absolute -top-1 -left-1 w-full h-full border border-gold" />
                        <div className="relative bg-white/30 w-full h-full" />
                      </div>
                    ),
                  },
                  {
                    value: 'grid',
                    label: 'Grid',
                    hint: '2×2 photo grid',
                    preview: (
                      <div className="grid grid-cols-2 gap-1 h-16">
                        <div className="bg-white/30" />
                        <div className="bg-white/30" />
                        <div className="bg-white/30" />
                        <div className="bg-white/30" />
                      </div>
                    ),
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSettings({ ...settings, about_layout: opt.value })}
                    data-testid={`about-layout-${opt.value}`}
                    className={`p-3 border text-left transition-colors ${
                      (settings.about_layout || 'collage') === opt.value
                        ? 'border-gold bg-gold/10'
                        : 'border-white/10 bg-white/5 hover:border-white/30'
                    }`}
                  >
                    {opt.preview}
                    <span className="block text-white text-sm mt-2 font-medium">{opt.label}</span>
                    <span className="block text-white/40 text-xs">{opt.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Images */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-white/60 text-sm">Images</label>
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, about_images: [...aboutImages, ''] })}
                  data-testid="about-add-image"
                  className="flex items-center gap-1.5 text-xs bg-white/10 text-white/70 px-3 py-1.5 hover:bg-gold/20 hover:text-gold transition-colors"
                >
                  <Plus size={14} /> Add Image
                </button>
              </div>
              <p className="text-white/40 text-xs mb-3">
                Leave the list empty to use the default photos. Collage uses the first 3 images,
                Grid the first 4, Single the first one, and the Slider shows all of them.
              </p>
              <div className="space-y-3">
                {aboutImages.map((url, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <span className="text-white/40 text-sm pt-3 w-5 text-center">{index + 1}</span>
                    <div className="flex-1">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={url}
                          onChange={(e) => setAboutImage(index, e.target.value)}
                          className="flex-1 bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:border-gold outline-none text-sm"
                          placeholder="https://... or upload"
                        />
                        <label className="flex items-center gap-2 bg-white/10 text-white/70 px-3 cursor-pointer hover:bg-white/20 transition-colors">
                          <Upload size={15} />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleAboutImageUpload(e, index)}
                          />
                        </label>
                      </div>
                      {url && (
                        <img
                          src={url}
                          alt={`About ${index + 1}`}
                          className="mt-2 h-16 w-auto object-cover border border-white/10"
                        />
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => moveAboutImage(index, -1)}
                        disabled={index === 0}
                        className="p-1.5 bg-white/5 text-white/50 hover:text-white disabled:opacity-30 transition-colors"
                        aria-label="Move up"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveAboutImage(index, 1)}
                        disabled={index === aboutImages.length - 1}
                        className="p-1.5 bg-white/5 text-white/50 hover:text-white disabled:opacity-30 transition-colors"
                        aria-label="Move down"
                      >
                        <ArrowDown size={14} />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAboutImage(index)}
                      className="p-1.5 mt-1 bg-white/5 text-red-400/70 hover:text-red-400 transition-colors"
                      aria-label="Remove image"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
                {aboutImages.length === 0 && (
                  <p className="text-white/30 text-sm italic">No custom images — default photos are shown.</p>
                )}
              </div>
            </div>

            {/* Experience badge */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/60 text-sm font-medium">Experience Badge (gold box)</p>
                <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.about_show_badge !== false}
                    onChange={(e) => setSettings({ ...settings, about_show_badge: e.target.checked })}
                    data-testid="about-show-badge"
                    className="accent-[#c9a227] w-4 h-4"
                  />
                  Show badge
                </label>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-white/50 text-xs mb-2">Value</label>
                  <input
                    type="text"
                    value={settings.about_badge_value || ''}
                    onChange={(e) => setSettings({ ...settings, about_badge_value: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    placeholder="10+"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-2">Label (English)</label>
                  <input
                    type="text"
                    value={settings.about_badge_label_en || ''}
                    onChange={(e) => setSettings({ ...settings, about_badge_label_en: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    placeholder="Years Experience"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-2">Label (Arabic)</label>
                  <input
                    type="text"
                    value={settings.about_badge_label_ar || ''}
                    onChange={(e) => setSettings({ ...settings, about_badge_label_ar: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    dir="rtl"
                    placeholder="سنوات خبرة"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transformation Section */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-2">Transformation Section</h2>
          <p className="text-white/40 text-sm mb-6">Controls the before/after slider on the homepage</p>

          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Title (English)</label>
                <input
                  type="text"
                  value={settings.transformation_title_en || ''}
                  onChange={(e) => setSettings({ ...settings, transformation_title_en: e.target.value })}
                  data-testid="settings-transformation-title-en"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  placeholder="See The Transformation"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Title (Arabic)</label>
                <input
                  type="text"
                  value={settings.transformation_title_ar || ''}
                  onChange={(e) => setSettings({ ...settings, transformation_title_ar: e.target.value })}
                  data-testid="settings-transformation-title-ar"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  dir="rtl"
                  placeholder="شاهد التحول"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Before Image</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={settings.transformation_before_image || ''}
                  onChange={(e) => setSettings({ ...settings, transformation_before_image: e.target.value })}
                  data-testid="settings-transformation-before"
                  className="flex-1 bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  placeholder="https://..."
                />
                <label className="flex items-center gap-2 bg-white/10 text-white/70 px-4 cursor-pointer hover:bg-white/20 transition-colors">
                  <Upload size={16} />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleTransformationUpload(e, 'transformation_before_image')}
                  />
                </label>
              </div>
              {settings.transformation_before_image && (
                <img
                  src={settings.transformation_before_image}
                  alt="Before preview"
                  className="mt-2 h-24 w-auto object-cover border border-white/10"
                />
              )}
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">After Image</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={settings.transformation_after_image || ''}
                  onChange={(e) => setSettings({ ...settings, transformation_after_image: e.target.value })}
                  data-testid="settings-transformation-after"
                  className="flex-1 bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  placeholder="https://..."
                />
                <label className="flex items-center gap-2 bg-white/10 text-white/70 px-4 cursor-pointer hover:bg-white/20 transition-colors">
                  <Upload size={16} />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleTransformationUpload(e, 'transformation_after_image')}
                  />
                </label>
              </div>
              {settings.transformation_after_image && (
                <img
                  src={settings.transformation_after_image}
                  alt="After preview"
                  className="mt-2 h-24 w-auto object-cover border border-white/10"
                />
              )}
            </div>
          </div>
        </div>

        {/* Agreement Template */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-2">Agreement Template</h2>
          <p className="text-white/40 text-sm mb-6">
            Displayed to customers on the Agreement step. HTML formatting is supported (bold, lists, etc.).
          </p>

          <div className="space-y-6">
            <div>
              <label className="block text-white/60 text-sm mb-2">Agreement Text (English)</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  { label: 'B', tag: 'strong' },
                  { label: 'I', tag: 'em' },
                  { label: 'U', tag: 'u' },
                  { label: '• List', tag: 'ul-li' },
                  { label: 'H3', tag: 'h3' },
                ].map(({ label, tag }) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      const insert = tag === 'ul-li'
                        ? '<ul>\n  <li>Item 1</li>\n  <li>Item 2</li>\n</ul>'
                        : `<${tag}>text</${tag}>`;
                      setSettings((s) => ({
                        ...s,
                        agreement_template_en: (s.agreement_template_en || '') + insert,
                      }));
                    }}
                    className="px-2.5 py-1 text-xs font-bold bg-white/10 text-white/70 hover:bg-gold/20 hover:text-gold border border-white/10 transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <textarea
                rows={8}
                value={settings.agreement_template_en || ''}
                onChange={(e) => setSettings({ ...settings, agreement_template_en: e.target.value })}
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none font-mono text-sm resize-y"
                placeholder="<h3>Kitchen Agreement</h3>&#10;<p>This agreement is made between...</p>"
                dir="ltr"
              />
              {settings.agreement_template_en && (
                <div className="mt-2 p-4 bg-white/3 border border-white/8 text-white/70 text-sm">
                  <p className="text-white/40 text-xs mb-2 uppercase tracking-wider">Preview</p>
                  <div
                    className="prose prose-invert prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(settings.agreement_template_en) }}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Agreement Text (Arabic)</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  { label: 'غامق', tag: 'strong' },
                  { label: 'مائل', tag: 'em' },
                  { label: '• قائمة', tag: 'ul-li' },
                  { label: 'عنوان', tag: 'h3' },
                ].map(({ label, tag }) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      const insert = tag === 'ul-li'
                        ? '<ul>\n  <li>البند الأول</li>\n  <li>البند الثاني</li>\n</ul>'
                        : `<${tag}>النص</${tag}>`;
                      setSettings((s) => ({
                        ...s,
                        agreement_template_ar: (s.agreement_template_ar || '') + insert,
                      }));
                    }}
                    className="px-2.5 py-1 text-xs font-bold bg-white/10 text-white/70 hover:bg-gold/20 hover:text-gold border border-white/10 transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <textarea
                rows={8}
                value={settings.agreement_template_ar || ''}
                onChange={(e) => setSettings({ ...settings, agreement_template_ar: e.target.value })}
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none font-mono text-sm resize-y"
                placeholder="<h3>اتفاقية المطبخ</h3>&#10;<p>هذه الاتفاقية مبرمة بين...</p>"
                dir="rtl"
              />
              {settings.agreement_template_ar && (
                <div className="mt-2 p-4 bg-white/3 border border-white/8 text-white/70 text-sm" dir="rtl">
                  <p className="text-white/40 text-xs mb-2 uppercase tracking-wider">معاينة</p>
                  <div
                    className="prose prose-invert prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(settings.agreement_template_ar) }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-2">Footer</h2>
          <p className="text-white/40 text-sm mb-6">
            Controls the footer at the bottom of every page. Phone, email, address, and social links are
            taken from the Contact Information and Social Media sections above. Leave a field empty to keep the site default.
          </p>

          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">About Text (English)</label>
                <textarea
                  rows={3}
                  value={settings.footer_description_en || ''}
                  onChange={(e) => setSettings({ ...settings, footer_description_en: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none resize-y"
                  placeholder="Crafting culinary masterpieces in Saudi Arabia..."
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">About Text (Arabic)</label>
                <textarea
                  rows={3}
                  value={settings.footer_description_ar || ''}
                  onChange={(e) => setSettings({ ...settings, footer_description_ar: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none resize-y"
                  dir="rtl"
                  placeholder="نصنع تحفاً فنية في المملكة العربية السعودية..."
                />
              </div>
            </div>

            <div className="border-t border-white/10 pt-4">
              <p className="text-white/60 text-sm mb-3 font-medium">Working Hours</p>
              <div className="space-y-3">
                <div className="grid md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    value={settings.footer_hours_line1_label_en || ''}
                    onChange={(e) => setSettings({ ...settings, footer_hours_line1_label_en: e.target.value })}
                    className="bg-white/5 border border-white/10 text-white px-3 py-2 focus:border-gold outline-none"
                    placeholder="Sat - Thu (EN)"
                  />
                  <input
                    type="text"
                    value={settings.footer_hours_line1_label_ar || ''}
                    onChange={(e) => setSettings({ ...settings, footer_hours_line1_label_ar: e.target.value })}
                    className="bg-white/5 border border-white/10 text-white px-3 py-2 focus:border-gold outline-none"
                    dir="rtl"
                    placeholder="السبت - الخميس (AR)"
                  />
                  <input
                    type="text"
                    value={settings.footer_hours_line1_value_en || ''}
                    onChange={(e) => setSettings({ ...settings, footer_hours_line1_value_en: e.target.value })}
                    className="bg-white/5 border border-white/10 text-white px-3 py-2 focus:border-gold outline-none"
                    placeholder="9:00 - 21:00 (EN)"
                  />
                  <input
                    type="text"
                    value={settings.footer_hours_line1_value_ar || ''}
                    onChange={(e) => setSettings({ ...settings, footer_hours_line1_value_ar: e.target.value })}
                    className="bg-white/5 border border-white/10 text-white px-3 py-2 focus:border-gold outline-none"
                    dir="rtl"
                    placeholder="9:00 - 21:00 (AR)"
                  />
                </div>
                <div className="grid md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    value={settings.footer_hours_line2_label_en || ''}
                    onChange={(e) => setSettings({ ...settings, footer_hours_line2_label_en: e.target.value })}
                    className="bg-white/5 border border-white/10 text-white px-3 py-2 focus:border-gold outline-none"
                    placeholder="Friday (EN)"
                  />
                  <input
                    type="text"
                    value={settings.footer_hours_line2_label_ar || ''}
                    onChange={(e) => setSettings({ ...settings, footer_hours_line2_label_ar: e.target.value })}
                    className="bg-white/5 border border-white/10 text-white px-3 py-2 focus:border-gold outline-none"
                    dir="rtl"
                    placeholder="الجمعة (AR)"
                  />
                  <input
                    type="text"
                    value={settings.footer_hours_line2_value_en || ''}
                    onChange={(e) => setSettings({ ...settings, footer_hours_line2_value_en: e.target.value })}
                    className="bg-white/5 border border-white/10 text-white px-3 py-2 focus:border-gold outline-none"
                    placeholder="Closed (EN)"
                  />
                  <input
                    type="text"
                    value={settings.footer_hours_line2_value_ar || ''}
                    onChange={(e) => setSettings({ ...settings, footer_hours_line2_value_ar: e.target.value })}
                    className="bg-white/5 border border-white/10 text-white px-3 py-2 focus:border-gold outline-none"
                    dir="rtl"
                    placeholder="مغلق (AR)"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 pt-4 grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Copyright Name</label>
                <input
                  type="text"
                  value={settings.footer_copyright_name || ''}
                  onChange={(e) => setSettings({ ...settings, footer_copyright_name: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  placeholder="Arman Kitchens"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Privacy Policy Link</label>
                <input
                  type="text"
                  value={settings.footer_privacy_url || ''}
                  onChange={(e) => setSettings({ ...settings, footer_privacy_url: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  placeholder="/privacy"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Terms & Conditions Link</label>
                <input
                  type="text"
                  value={settings.footer_terms_url || ''}
                  onChange={(e) => setSettings({ ...settings, footer_terms_url: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  placeholder="/terms"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={saving}
          data-testid="save-settings-btn"
          whileTap={{ scale: 0.98 }}
          className={`flex items-center justify-center gap-2 w-full py-4 font-medium transition-colors ${
            saved 
              ? 'bg-green-500 text-white' 
              : 'bg-gold text-charcoal hover:bg-gold/90'
          }`}
        >
          {saving ? (
            <span className="w-5 h-5 border-2 border-charcoal border-t-transparent rounded-full animate-spin" />
          ) : saved ? (
            <>
              <CheckCircle size={20} />
              Settings Saved
            </>
          ) : (
            <>
              <Save size={20} />
              Save Settings
            </>
          )}
        </motion.button>
      </form>
    </div>
  );
};

export default AdminSettings;
